import { useState } from "react";
import axios from "axios";
import { User, Key, LogOut, Mail, Hash, Phone, Loader2, ChevronRight } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/apiConfig";

export default function MePage({ profile, onLogout, onRefresh }) {
  const [section, setSection] = useState(null);
  const [email, setEmail] = useState(profile?.email || "");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter an email");
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${API}/user/update-email`, { email: email.trim() });
      toast.success("Email updated successfully");
      onRefresh();
      setSection(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!oldPassword || !newPassword) {
      toast.error("Fill in both fields");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/user/reset-password`,
        { old_password: oldPassword, new_password: newPassword },
        {  }
      );
      toast.success("Password updated successfully");
      setOldPassword("");
      setNewPassword("");
      setSection(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch {
      // ignore
    }
    localStorage.removeItem("access_token");
    toast.success("Logged out");
    onLogout();
  };

  if (section === "settings") {
    return (
      <div className="me-page" data-testid="me-settings">
        <h1 className="page-title heading-serif">User Settings</h1>
        <button className="back-btn" onClick={() => setSection(null)} data-testid="back-btn">
          &larr; Back
        </button>

        <div className="settings-info" data-testid="settings-info">
          <div className="settings-row">
            <Phone size={16} className="text-[#d4af37]" />
            <span className="settings-label">Mobile Number</span>
            <span className="settings-value">{profile?.masked_phone || "------"}</span>
          </div>
          <div className="settings-row">
            <Hash size={16} className="text-[#d4af37]" />
            <span className="settings-label">User ID</span>
            <span className="settings-value">{profile?.user_id || "------"}</span>
          </div>
          <div className="settings-row">
            <Mail size={16} className="text-[#d4af37]" />
            <span className="settings-label">Email</span>
            <span className="settings-value">{profile?.email || "Not set"}</span>
          </div>
        </div>

        <form onSubmit={handleUpdateEmail} className="mt-6" data-testid="email-update-form">
          <label className="auth-label">Update Email</label>
          <input
            type="email"
            className="auth-input mb-3"
            placeholder="Enter email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="email-input"
          />
          <button type="submit" className="btn-gold" disabled={loading} data-testid="email-submit-btn">
            {loading ? <Loader2 size={18} className="animate-spin mx-auto" /> : "Update Email"}
          </button>
        </form>
      </div>
    );
  }

  if (section === "password") {
    return (
      <div className="me-page" data-testid="me-password">
        <h1 className="page-title heading-serif">Reset Password</h1>
        <button className="back-btn" onClick={() => setSection(null)} data-testid="back-btn">
          &larr; Back
        </button>
        <form onSubmit={handleResetPassword} className="mt-4 space-y-4" data-testid="password-reset-form">
          <div>
            <label className="auth-label">Current Password</label>
            <input
              type="password"
              className="auth-input"
              placeholder="Enter current password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              data-testid="old-password-input"
            />
          </div>
          <div>
            <label className="auth-label">New Password</label>
            <input
              type="password"
              className="auth-input"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="new-password-input"
            />
          </div>
          <button type="submit" className="btn-gold" disabled={loading} data-testid="password-submit-btn">
            {loading ? <Loader2 size={18} className="animate-spin mx-auto" /> : "Update Password"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="me-page" data-testid="me-page">
      <h1 className="page-title heading-serif">My Account</h1>

      {/* User Info Card */}
      <div className="me-user-card" data-testid="me-user-card">
        <div className="user-avatar-circle large">
          <span>{profile?.masked_phone?.slice(0, 2) || "AP"}</span>
        </div>
        <div>
          <p className="text-white font-medium">ID: {profile?.user_id || "------"}</p>
          <p className="text-sm text-slate-400">{profile?.masked_phone || "------"}</p>
        </div>
      </div>

      {/* Menu Items */}
      <div className="me-menu" data-testid="me-menu">
        <button
          className="me-menu-item"
          onClick={() => setSection("settings")}
          data-testid="menu-settings"
        >
          <User size={18} className="text-[#d4af37]" />
          <span>User Settings</span>
          <ChevronRight size={16} className="ml-auto text-slate-500" />
        </button>

        <button
          className="me-menu-item"
          onClick={() => setSection("password")}
          data-testid="menu-password"
        >
          <Key size={18} className="text-[#d4af37]" />
          <span>Reset Password</span>
          <ChevronRight size={16} className="ml-auto text-slate-500" />
        </button>

        <button
          className="me-menu-item logout"
          onClick={handleLogout}
          data-testid="menu-logout"
        >
          <LogOut size={18} className="text-red-400" />
          <span className="text-red-400">Log Out</span>
          <ChevronRight size={16} className="ml-auto text-slate-500" />
        </button>
      </div>
    </div>
  );
}
